import { VoiceRecording, type RecordingState } from "./voice-recording";

export function initVoiceRecorder(options: { onBusy: (busy: boolean) => void; onRecording: (file: File) => Promise<void> }) {
  const region = document.querySelector<HTMLElement>("#voice-recorder");
  if (!region) return null;
  const record = region.querySelector<HTMLButtonElement>("#record-voice")!;
  const controls = region.querySelector<HTMLElement>("#recording-controls")!;
  const status = region.querySelector<HTMLElement>("#recording-status")!;
  const timer = region.querySelector<HTMLElement>("#recording-time")!;
  const stop = region.querySelector<HTMLButtonElement>("#stop-recording")!;
  const cancel = region.querySelector<HTMLButtonElement>("#cancel-recording")!;
  const help = region.querySelector<HTMLElement>("#recording-help")!;
  const error = region.querySelector<HTMLElement>("#recording-error")!;
  let disabled = false;
  let state: RecordingState = "idle";

  function render(): void {
    record.hidden = state !== "idle";
    record.disabled = disabled;
    controls.hidden = state === "idle";
    stop.hidden = state !== "recording";
    timer.hidden = state !== "recording";
    region!.classList.toggle("is-recording", state === "recording");
    status.textContent = state === "requesting" ? "Allow microphone access to start recording."
      : state === "recording" ? "Recording your voice…"
      : state === "preparing" ? "Preparing your recording…" : "";
  }

  const recording = new VoiceRecording({
    onState(next) {
      state = next;
      options.onBusy(state !== "idle");
      render();
      if (state === "recording") stop.focus({ preventScroll: true });
      else if (state !== "idle") cancel.focus({ preventScroll: true });
    },
    onTime(seconds) {
      timer.textContent = `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")} / 10:00`;
    },
    onError(message) {
      error.textContent = message;
      error.hidden = false;
      record.focus({ preventScroll: true });
    },
    async onRecording(file) {
      await options.onRecording(file);
      record.querySelector("span")!.textContent = "Record again";
    },
  });

  const supported = window.isSecureContext && Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== "undefined" && typeof OfflineAudioContext !== "undefined";
  if (!supported) {
    record.disabled = true;
    help.textContent = "Voice recording isn't available in this browser. You can still choose an audio file.";
    return { get busy() { return false; }, setDisabled() {}, cancel() {} };
  }
  record.addEventListener("click", () => {
    if (disabled) return;
    error.hidden = true;
    window.dispatchEvent(new Event("audio-recording-start"));
    void recording.start();
  });
  stop.addEventListener("click", () => recording.stop());
  cancel.addEventListener("click", () => {
    recording.cancel();
    record.focus({ preventScroll: true });
  });
  window.addEventListener("pagehide", () => recording.cancel());
  return {
    get busy() { return state !== "idle"; },
    setDisabled(value: boolean) { disabled = value; render(); },
    cancel() { recording.cancel(); },
  };
}
